import React from 'react';
import type { TeamResponse } from '../types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { MemberAvatarList, StatusPill, Switch } from '@/shared/components/ui';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useAddTeamUser, useRemoveTeamUser } from '../hooks';
import { Icon } from '@/shared/icons';

/**
 * Card de equipe (view "Cards"). Padrão-lista travado: card flat/borda SEM
 * checkbox (F34 — seleção em massa só na tabela) + menu ••• de ações + switch
 * inline de status (Switch shared sm — F47). Avatar da equipe = slate (#64748b
 * via AvatarFallback) com glyph de "users". A gestão inline de membros
 * (adicionar via dropdown) é preservada pelo MemberAvatarList.
 */
export interface TeamCardProps {
  /** Team data from API */
  team: TeamResponse;
  /** Members assigned to this team */
  members?: Array<{ id: string; name: string; avatar?: string }>;
  /** Callback when edit is clicked */
  onEdit?: (id: string) => void;
  /** Callback when delete is clicked */
  onDelete?: (id: string) => void;
  /** Callback when activate/deactivate is clicked */
  onToggleStatus?: (id: string, currentStatus: string) => void;
  /** Atalho para gerenciar membros (navega pro form da equipe) */
  onManageMembers?: (id: string) => void;
}

export const TeamCard: React.FC<TeamCardProps> = ({
  team,
  members = [],
  onEdit,
  onDelete,
  onToggleStatus,
  onManageMembers,
}) => {
  // Local state for instant updates (optimistic member add/remove)
  const [localMembers, setLocalMembers] = React.useState(members);
  React.useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  // Fetch available users for the add-member dropdown
  const { data: allUsers = [], isLoading: loadingUsers } = useUsersDropdown({ status: 'active' });
  const availableUsers = allUsers;

  const addUserMutation = useAddTeamUser();
  const removeUserMutation = useRemoveTeamUser();

  const handleAddMember = (userId: string) => {
    const newMember = availableUsers.find((u) => u.id === userId);
    if (newMember) setLocalMembers([...localMembers, newMember]);
    addUserMutation.mutate({ teamId: team.id, userId });
  };

  const handleRemoveMember = (userId: string) => {
    setLocalMembers(localMembers.filter((m) => m.id !== userId));
    removeUserMutation.mutate({ teamId: team.id, userId });
  };

  const isActive = team.status === 'active';

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(team.id) });
  if (onManageMembers) actions.push({ label: 'Gerenciar membros', icon: 'users', onClick: () => onManageMembers(team.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(team.id, team.status),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(team.id), variant: 'danger', dividerBefore: true });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`team-card-${team.id}`}
    >
      {/* Header: avatar + nome/descrição + ações */}
      <div className="flex items-start gap-3">
        <Avatar className="w-11 h-11 flex-shrink-0" isSquircle>
          <AvatarFallback isSquircle>
            <Icon name="users" className="w-5 h-5 text-white" />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {team.name}
          </h3>
          <p className="text-xs line-clamp-2" style={{ color: 'var(--theme-muted-text)' }}>
            {team.description || 'Sem descrição'}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`team-card-actions-${team.id}`} />
          </div>
        )}
      </div>

      {/* Status — F47: switch inline quando togglável; F62: StatusPill shared */}
      <div className="mt-3">
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`team-card-toggle-${team.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(team.id, team.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativa' : 'Inativa'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativa' : 'Inativa'}
            data-testid={`team-card-status-${team.id}`}
          />
        )}
      </div>

      {/* Membros (avatares + gestão inline) */}
      <div className="mt-3 pt-3 flex items-center gap-2" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        <MemberAvatarList
          members={localMembers}
          availableUsers={availableUsers}
          limit={4}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          canRemoveMembers={false}
          loading={loadingUsers}
          placeholder="Buscar membro..."
          emptyMessage="Sem membros"
        />
        <span className="ml-auto text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {localMembers.length} {localMembers.length === 1 ? 'membro' : 'membros'}
        </span>
      </div>
    </div>
  );
};

TeamCard.displayName = 'TeamCard';
